import React, { useState, useEffect } from 'react';

const ROLES = ['Founder & Owner', 'Director', 'Advisor', 'Member'];
const ROLE_CATEGORIES = {
  'Founder & Owner': 'founder',
  'Director': 'directors',
  'Advisor': 'advisors',
  'Member': 'members'
};

const DEFAULT_MEMBER = {
  twitch: '',
  display_name: '',
  role: 'Member',
  website: '',
  bio: ''
};

function TeamEditor({ data, onSave, showToast }) {
  const [teamData, setTeamData] = useState(null);
  const [editingMember, setEditingMember] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    if (data) {
      setTeamData(JSON.parse(JSON.stringify(data)));
    }
  }, [data]);

  const getAllMembers = () => {
    if (!teamData) return [];
    return [
      ...(teamData.founder || []).map(m => ({ ...m, category: 'founder' })),
      ...(teamData.directors || []).map(m => ({ ...m, category: 'directors' })),
      ...(teamData.advisors || []).map(m => ({ ...m, category: 'advisors' })),
      ...(teamData.members || []).map(m => ({ ...m, category: 'members' }))
    ];
  };

  const getMembersByCategory = (category) => {
    return teamData?.[category] || [];
  };

  const handleAddMember = () => {
    setEditingMember({ ...DEFAULT_MEMBER, isNew: true });
    setShowModal(true);
  };

  const handleEditMember = (member, category) => {
    setEditingMember({ ...member, originalCategory: category, isNew: false });
    setShowModal(true);
  };

  const handleDeleteMember = (member, category) => {
    if (!window.confirm(`Remove ${member.display_name || member.twitch} from the team?`)) {
      return;
    }

    const updatedData = { ...teamData };
    updatedData[category] = updatedData[category].filter(m => m.twitch !== member.twitch);
    setTeamData(updatedData);
    setHasChanges(true);
    showToast('Member removed. Click Save to confirm.', 'warning');
  };

  const handleSaveMember = (memberData) => {
    const updatedData = { ...teamData };
    const targetCategory = ROLE_CATEGORIES[memberData.role] || 'members';

    // Remove from original category if editing
    if (!memberData.isNew && memberData.originalCategory) {
      updatedData[memberData.originalCategory] = updatedData[memberData.originalCategory].filter(
        m => m.twitch !== editingMember.twitch
      );
    }

    // Clean up the member data
    const cleanMember = {
      twitch: memberData.twitch.toLowerCase().trim(),
      display_name: memberData.display_name.trim(),
      role: memberData.role,
      website: memberData.website.trim(),
      bio: memberData.bio.trim()
    };

    // Add to target category
    if (!updatedData[targetCategory]) {
      updatedData[targetCategory] = [];
    }
    updatedData[targetCategory].push(cleanMember);

    setTeamData(updatedData);
    setShowModal(false);
    setEditingMember(null);
    setHasChanges(true);
    showToast(`${cleanMember.display_name} ${memberData.isNew ? 'added' : 'updated'}. Click Save to confirm.`, 'warning');
  };

  const handleSaveAll = async () => {
    setSaving(true);
    const result = await onSave(teamData);
    setSaving(false);
    if (result.success) {
      setHasChanges(false);
    }
  };

  const categories = [
    { key: 'founder', title: 'Founder', color: 'var(--gold-500)' },
    { key: 'directors', title: 'Directors', color: 'var(--forest-600)' },
    { key: 'advisors', title: 'Advisors', color: 'var(--earth-500)' },
    { key: 'members', title: 'Members', color: 'var(--forest-400)' }
  ];

  if (!teamData) {
    return (
      <div className="empty-state">
        <div className="empty-state-title">Loading team data...</div>
      </div>
    );
  }

  return (
    <div>
      {/* Header with Save Button */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <div>
          <h2 style={{ fontSize: '1.25rem', fontWeight: '600', color: 'var(--forest-800)', margin: 0 }}>
            Team Members
          </h2>
          <p style={{ fontSize: '0.875rem', color: 'var(--forest-600)', marginTop: '0.25rem' }}>
            {getAllMembers().length} members across all roles
          </p>
        </div>
        <div style={{ display: 'flex', gap: '0.75rem' }}>
          <button className="btn-editor btn-editor-secondary" onClick={handleAddMember}>
            + Add Member
          </button>
          <button
            className="btn-editor btn-editor-primary"
            onClick={handleSaveAll}
            disabled={!hasChanges || saving}
            style={{ opacity: hasChanges ? 1 : 0.5 }}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>

      {/* Role Sections */}
      {categories.map(cat => (
        <div key={cat.key} className="role-section">
          <div className="role-header">
            <h3 className="role-title">
              <span style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                background: cat.color,
                display: 'inline-block'
              }}></span>
              {cat.title}
              <span className="role-count">{getMembersByCategory(cat.key).length}</span>
            </h3>
          </div>
          <div className="content-grid">
            {getMembersByCategory(cat.key).map(member => (
              <MemberCard
                key={member.twitch}
                member={member}
                category={cat.key}
                onEdit={() => handleEditMember(member, cat.key)}
                onDelete={() => handleDeleteMember(member, cat.key)}
              />
            ))}
            {cat.key === 'members' && getMembersByCategory(cat.key).length === 0 && (
              <div className="add-card" onClick={handleAddMember}>
                <div className="add-card-icon">+</div>
                <div className="add-card-text">Add Member</div>
              </div>
            )}
          </div>
        </div>
      ))}

      {/* Edit Modal */}
      {showModal && editingMember && (
        <MemberModal
          member={editingMember}
          onSave={handleSaveMember}
          onClose={() => {
            setShowModal(false);
            setEditingMember(null);
          }}
          existingUsernames={getAllMembers().map(m => m.twitch).filter(t => t !== editingMember.twitch)}
        />
      )}
    </div>
  );
}

function MemberCard({ member, category, onEdit, onDelete }) {
  const profileImage = member.profile_image_url ||
    `https://static-cdn.jtvnw.net/user-default-pictures-uv/cdd517fe-def4-11e9-948e-784f43822e80-profile_image-70x70.png`;

  return (
    <div className="content-card">
      <div className="content-card-header">
        <img
          src={profileImage}
          alt={member.display_name}
          className="content-card-avatar"
          onError={(e) => {
            e.target.src = 'https://static-cdn.jtvnw.net/user-default-pictures-uv/cdd517fe-def4-11e9-948e-784f43822e80-profile_image-70x70.png';
          }}
        />
        <div className="content-card-info">
          <div className="content-card-name">{member.display_name || member.twitch}</div>
          <div className="content-card-role">{member.role}</div>
        </div>
        <div className="content-card-actions">
          <button className="btn-editor btn-icon btn-editor-ghost" onClick={onEdit} title="Edit">
            Edit
          </button>
          <button className="btn-editor btn-icon btn-editor-danger" onClick={onDelete} title="Delete">
            X
          </button>
        </div>
      </div>
      {member.bio && (
        <div className="content-card-body" style={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical'
        }}>
          {member.bio}
        </div>
      )}
      <div style={{ marginTop: '0.5rem', fontSize: '0.75rem', color: 'var(--forest-500)' }}>
        twitch.tv/{member.twitch}
      </div>
    </div>
  );
}

function MemberModal({ member, onSave, onClose, existingUsernames }) {
  const [formData, setFormData] = useState({
    twitch: member.twitch || '',
    display_name: member.display_name || '',
    role: member.role || 'Member',
    website: member.website || '',
    bio: member.bio || '',
    isNew: member.isNew,
    originalCategory: member.originalCategory
  });
  const [errors, setErrors] = useState({});

  const validate = () => {
    const newErrors = {};

    if (!formData.twitch.trim()) {
      newErrors.twitch = 'Twitch username is required';
    } else if (!/^[a-zA-Z0-9_]+$/.test(formData.twitch.trim())) {
      newErrors.twitch = 'Only letters, numbers, and underscores allowed';
    } else if (existingUsernames.includes(formData.twitch.toLowerCase().trim())) {
      newErrors.twitch = 'This username is already in the team';
    }

    if (!formData.display_name.trim()) {
      newErrors.display_name = 'Display name is required';
    }

    if (formData.website && !formData.website.match(/^https?:\/\/.+/)) {
      newErrors.website = 'Must be a valid URL (https://...)';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validate()) {
      onSave(formData);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">{member.isNew ? 'Add Team Member' : 'Edit Member'}</h2>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-group">
              <label className="form-label">Twitch Username *</label>
              <input
                type="text"
                className={`form-input ${errors.twitch ? 'error' : ''}`}
                value={formData.twitch}
                onChange={(e) => setFormData({ ...formData, twitch: e.target.value })}
                placeholder="username"
              />
              {errors.twitch && <div className="form-error">{errors.twitch}</div>}
            </div>

            <div className="form-group">
              <label className="form-label">Display Name *</label>
              <input
                type="text"
                className={`form-input ${errors.display_name ? 'error' : ''}`}
                value={formData.display_name}
                onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
                placeholder="How their name appears on the site"
              />
              {errors.display_name && <div className="form-error">{errors.display_name}</div>}
            </div>

            <div className="form-group">
              <label className="form-label">Role</label>
              <select
                className="form-input form-select"
                value={formData.role}
                onChange={(e) => setFormData({ ...formData, role: e.target.value })}
              >
                {ROLES.map(role => (
                  <option key={role} value={role}>{role}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Website (optional)</label>
              <input
                type="text"
                className={`form-input ${errors.website ? 'error' : ''}`}
                value={formData.website}
                onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                placeholder="https://..."
              />
              {errors.website && <div className="form-error">{errors.website}</div>}
            </div>

            <div className="form-group">
              <label className="form-label">Bio (optional)</label>
              <textarea
                className="form-input form-textarea"
                value={formData.bio}
                onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                placeholder="A short bio about this team member"
                rows={3}
              />
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn-editor btn-editor-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn-editor btn-editor-primary">
              {member.isNew ? 'Add Member' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default TeamEditor;
